import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  LuChevronLeft,
  LuChevronRight,
  LuEye,
  LuFileText,
  LuMessageSquareHeart,
  LuPaperclip,
  LuRefreshCw,
  LuSearch,
  LuStar,
  LuUserCheck,
  LuUserCog,
  LuUserPlus,
  LuX,
} from "react-icons/lu";
import { resolvePublicFileUrl } from "../../constants/api-urls";
import {
  FEEDBACK_ASSIGNMENT_OPTIONS,
  FEEDBACK_STATUS_OPTIONS,
  FEEDBACK_TYPE_OPTIONS,
  assignManagerFeedback,
  feedbackExtractMessage,
  fetchManagerFeedback,
  fetchManagerFeedbackAssignees,
  fetchManagerFeedbackById,
  fetchSuperAdminFeedback,
  fetchSuperAdminFeedbackById,
} from "../../services/platformFeedback.service";
import "../../assets/styles/super_admin_access.scss";

const PAGE_OPTIONS = [10, 20, 50];

const TAB_OPTIONS = [
  { id: "all", label: "All Feedback" },
  { id: "unassigned", label: "Unassigned" },
  { id: "assigned", label: "Assigned" },
];

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date
    .toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
    .replace(",", "");
}

function formatStatus(status) {
  return String(status || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function truncateText(value, max = 28) {
  const text = String(value || "").trim();
  if (!text) return "—";
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}

function nameInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "SU";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function assignmentBadge(status) {
  if (status === "assigned") {
    return { label: "Assigned", className: "sa-dispute-badge sa-dispute-badge--manual" };
  }
  return { label: "Unassigned", className: "sa-dispute-badge sa-dispute-badge--unassigned" };
}

function statusBadge(status) {
  const normalized = String(status || "").toLowerCase();
  return `sa-dispute-status sa-dispute-status--${normalized.replace(/\s+/g, "_")}`;
}

function buildPageNumbers(currentPage, totalPages, maxVisible = 7) {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }
  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages, start + maxVisible - 1);
  start = Math.max(1, end - maxVisible + 1);
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}

export default function SuperAdminFeedbackManagementPage({
  basePath = "/super-admin",
  canAssign = false,
}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState(() => searchParams.get("status") || "");
  const [feedbackType, setFeedbackType] = useState(() => searchParams.get("feedbackType") || "");
  const [assignmentStatus, setAssignmentStatus] = useState(
    () => searchParams.get("assignmentStatus") || "",
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [viewFeedback, setViewFeedback] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState("");
  const [assignees, setAssignees] = useState([]);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assigneeId, setAssigneeId] = useState("");
  const [assignReason, setAssignReason] = useState("");
  const [assignNotes, setAssignNotes] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [globalStats, setGlobalStats] = useState({
    total: 0,
    unassigned: 0,
    assigned: 0,
  });

  const listFeedback = canAssign ? fetchManagerFeedback : fetchSuperAdminFeedback;
  const getFeedbackById = canAssign ? fetchManagerFeedbackById : fetchSuperAdminFeedbackById;
  const feedbackBasePath = `${basePath}/feedback`;
  const dashboardPath = canAssign ? "/home" : "/super-admin/dashboard";

  const activeTab = useMemo(() => {
    if (assignmentStatus === "unassigned") return "unassigned";
    if (assignmentStatus === "assigned") return "assigned";
    return "all";
  }, [assignmentStatus]);

  const syncUrlFilters = (next) => {
    const params = new URLSearchParams();
    if (next.status) params.set("status", next.status);
    if (next.feedbackType) params.set("feedbackType", next.feedbackType);
    if (next.assignmentStatus) params.set("assignmentStatus", next.assignmentStatus);
    setSearchParams(params, { replace: true });
  };

  const resetFilters = () => {
    setQuery("");
    setStatus("");
    setFeedbackType("");
    setAssignmentStatus("");
    setPage(1);
    setSearchParams({}, { replace: true });
  };

  const handleTabChange = (tabId) => {
    setPage(1);
    if (tabId === "all") {
      setAssignmentStatus("");
      syncUrlFilters({ status, feedbackType });
      return;
    }
    if (tabId === "unassigned") {
      setAssignmentStatus("unassigned");
      syncUrlFilters({ assignmentStatus: "unassigned", status, feedbackType });
      return;
    }
    if (tabId === "assigned") {
      setAssignmentStatus("assigned");
      syncUrlFilters({ assignmentStatus: "assigned", status, feedbackType });
    }
  };

  useEffect(() => {
    setStatus(searchParams.get("status") || "");
    setFeedbackType(searchParams.get("feedbackType") || "");
    setAssignmentStatus(searchParams.get("assignmentStatus") || "");
  }, [searchParams]);

  const loadGlobalStats = useCallback(async () => {
    try {
      const [allRes, unassignedRes, assignedRes] = await Promise.all([
        listFeedback({ page: 1, pageSize: 1 }),
        listFeedback({ assignmentStatus: "unassigned", page: 1, pageSize: 1 }),
        listFeedback({ assignmentStatus: "assigned", page: 1, pageSize: 1 }),
      ]);
      setGlobalStats({
        total: Math.trunc(Number(allRes.total) || 0),
        unassigned: Math.trunc(Number(unassignedRes.total) || 0),
        assigned: Math.trunc(Number(assignedRes.total) || 0),
      });
    } catch {
      // Keep prior stats on failure.
    }
  }, [listFeedback]);

  useEffect(() => {
    loadGlobalStats();
  }, [loadGlobalStats]);

  useEffect(() => {
    if (!canAssign) return undefined;
    let cancelled = false;
    fetchManagerFeedbackAssignees()
      .then((data) => {
        if (!cancelled) setAssignees(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setAssignees([]);
      });
    return () => {
      cancelled = true;
    };
  }, [canAssign]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listFeedback({
        search: query.trim() || undefined,
        status: status || undefined,
        feedbackType: feedbackType || undefined,
        assignmentStatus: assignmentStatus || undefined,
        page,
        pageSize,
      });
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotal(Math.trunc(Number(data.total) || 0));
    } catch (error) {
      toast.error(feedbackExtractMessage(error, "Failed to load feedback."));
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [assignmentStatus, feedbackType, listFeedback, page, pageSize, query, status]);

  useEffect(() => {
    const timer = setTimeout(loadList, 250);
    return () => clearTimeout(timer);
  }, [loadList]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageNumbers = useMemo(
    () => buildPageNumbers(currentPage, totalPages),
    [currentPage, totalPages],
  );

  const refreshAll = async () => {
    await Promise.all([loadList(), loadGlobalStats()]);
  };

  const handleView = (row) => {
    navigate(`${feedbackBasePath}/${row.id}`);
  };

  const closeQuickView = () => {
    setViewFeedback(null);
    setViewLoading(false);
    setViewError("");
  };

  const openQuickView = async (row) => {
    const id = Number(row?.id);
    if (!Number.isFinite(id) || id <= 0) return;
    setViewFeedback({ feedback: row, attachments: [], activities: [], messages: [] });
    setViewError("");
    setViewLoading(true);
    try {
      const detail = await getFeedbackById(id);
      if (detail?.feedback) {
        setViewFeedback(detail);
      } else {
        setViewError("Feedback details are unavailable.");
      }
    } catch (error) {
      setViewError(feedbackExtractMessage(error, "Failed to load feedback details."));
    } finally {
      setViewLoading(false);
    }
  };

  const openAssignModal = (row) => {
    setAssignTarget(row);
    setAssigneeId(row.assignedToAccessUserId || "");
    setAssignReason("");
    setAssignNotes("");
  };

  const submitAssign = async () => {
    if (!assignTarget || !assigneeId) {
      toast.error("Select a support user.");
      return;
    }
    setAssigning(true);
    setBusyId(assignTarget.id);
    try {
      await assignManagerFeedback(assignTarget.id, {
        accessUserId: Number(assigneeId),
        reason: assignReason.trim() || undefined,
        notes: assignNotes.trim() || undefined,
      });
      toast.success(`Feedback ${assignTarget.feedbackNumber} assigned successfully.`);
      setAssignTarget(null);
      setAssigneeId("");
      setAssignReason("");
      setAssignNotes("");
      await Promise.all([loadList(), loadGlobalStats()]);
    } catch (error) {
      toast.error(feedbackExtractMessage(error, "Failed to assign feedback."));
    } finally {
      setAssigning(false);
      setBusyId(null);
    }
  };

  return (
    <div className="sa-access-page sa-dispute-page">
      <header className="sa-access-head">
        <div>
          <nav className="sa-access-breadcrumb" aria-label="Breadcrumb">
            <Link to={dashboardPath}>Dashboard</Link>
            <span aria-hidden>&gt;</span>
            <span aria-current="page">Feedback Management</span>
          </nav>
          <h1>Feedback Management</h1>
          <p>
            {canAssign
              ? "Assign feedback to support users and track review status."
              : "Read-only oversight of platform feedback, assignments, and audit history."}
          </p>
        </div>
        <div className="sa-access-actions">
          <button type="button" className="sa-access-btn sa-access-btn--ghost" onClick={refreshAll}>
            <LuRefreshCw aria-hidden /> Refresh
          </button>
        </div>
      </header>

      <section className="sa-access-stats">
        <article className="sa-access-stat">
          <span className="sa-access-stat__icon sa-access-stat__icon--blue">
            <LuFileText />
          </span>
          <div>
            <strong>{globalStats.total.toLocaleString()}</strong>
            <span>Total Feedback</span>
          </div>
        </article>
        <article className="sa-access-stat">
          <span className="sa-access-stat__icon sa-access-stat__icon--orange">
            <LuUserPlus />
          </span>
          <div>
            <strong>{globalStats.unassigned.toLocaleString()}</strong>
            <span>Unassigned</span>
          </div>
        </article>
        <article className="sa-access-stat">
          <span className="sa-access-stat__icon sa-access-stat__icon--green">
            <LuUserCheck />
          </span>
          <div>
            <strong>{globalStats.assigned.toLocaleString()}</strong>
            <span>Assigned</span>
          </div>
        </article>
        <article className="sa-access-stat">
          <span className="sa-access-stat__icon sa-access-stat__icon--purple">
            <LuMessageSquareHeart />
          </span>
          <div>
            <strong>{items.length.toLocaleString()}</strong>
            <span>On This Page</span>
          </div>
        </article>
      </section>

      <div
        className="sa-access-toolbar sa-dispute-toolbar"
        style={{
          gridTemplateColumns: "minmax(220px, 1.6fr) repeat(3, minmax(140px, 1fr)) auto",
        }}
      >
        <label className="sa-access-search">
          <LuSearch aria-hidden />
          <input
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(1);
            }}
            placeholder="Search by Feedback ID, Customer, Subject..."
          />
        </label>
        <select
          value={status}
          onChange={(event) => {
            const next = event.target.value;
            setStatus(next);
            setPage(1);
            syncUrlFilters({
              status: next,
              feedbackType,
              assignmentStatus,
            });
          }}
        >
          {FEEDBACK_STATUS_OPTIONS.map((opt) => (
            <option key={opt.value || "all-status"} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <select
          value={feedbackType}
          onChange={(event) => {
            const next = event.target.value;
            setFeedbackType(next);
            setPage(1);
            syncUrlFilters({
              status,
              feedbackType: next,
              assignmentStatus,
            });
          }}
        >
          {FEEDBACK_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value || "all-type"} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <select
          value={assignmentStatus}
          onChange={(event) => {
            const next = event.target.value;
            setAssignmentStatus(next);
            setPage(1);
            syncUrlFilters({
              status,
              feedbackType,
              assignmentStatus: next,
            });
          }}
        >
          {FEEDBACK_ASSIGNMENT_OPTIONS.map((opt) => (
            <option key={opt.value || "all-assignment"} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <button type="button" className="sa-access-btn sa-access-btn--ghost" onClick={resetFilters}>
          Reset
        </button>
      </div>

      <section className="sa-access-card sa-dispute-card">
        <div className="sa-dispute-card-toolbar">
          <div className="sa-dispute-tabs" role="tablist" aria-label="Feedback views">
            {TAB_OPTIONS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                className={`sa-dispute-tabs__btn${activeTab === tab.id ? " sa-dispute-tabs__btn--active" : ""}`}
                onClick={() => handleTabChange(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="sa-access-table-wrap">
          <table className="sa-access-table sa-dispute-table">
            <thead>
              <tr>
                <th>Feedback ID</th>
                <th>Subject</th>
                <th>Customer</th>
                <th>Type</th>
                <th>Rating</th>
                <th>Status</th>
                <th>Submitted</th>
                <th>Assignment</th>
                <th>Assignee</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className="sa-access-table__empty">
                    Loading feedback...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={10} className="sa-access-table__empty">
                    No feedback found.
                  </td>
                </tr>
              ) : (
                items.map((row) => {
                  const isAssigned = row.assignmentStatus === "assigned";
                  const isUnassigned = !isAssigned;
                  const canShowAssignAction =
                    canAssign && String(row.status || "").toLowerCase() !== "closed";
                  return (
                    <tr key={row.id}>
                      <td>
                        <div className="sa-dispute-id-cell">
                          <button
                            type="button"
                            className="sa-dispute-id-link"
                            title={row.feedbackNumber}
                            onClick={() => openQuickView(row)}
                          >
                            {row.feedbackNumber}
                          </button>
                          <span className="sa-dispute-id-chip">
                            #{String(row.id).padStart(6, "0")}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="sa-dispute-stack">
                          <span className="sa-dispute-stack__title" title={row.subject || undefined}>
                            {truncateText(row.subject || "—", 28)}
                          </span>
                          {row.areaLabel ? (
                            <span className="sa-dispute-stack__meta" title={row.areaLabel}>
                              {truncateText(row.areaLabel, 22)}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td>
                        <div className="sa-dispute-stack">
                          <span className="sa-dispute-stack__title" title={row.customerName}>
                            {truncateText(row.customerName, 20)}
                          </span>
                          <span
                            className="sa-dispute-stack__meta"
                            title={row.customerEmail || undefined}
                          >
                            {truncateText(row.customerEmail || "—", 24)}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className="sa-dispute-stack__meta">
                          {truncateText(row.feedbackType || "—", 22)}
                        </span>
                      </td>
                      <td>
                        <span className="sa-dispute-stack__meta">
                          <LuStar aria-hidden style={{ marginRight: 4, color: "#f59e0b" }} />
                          {row.rating || 0}
                        </span>
                      </td>
                      <td>
                        <span className={statusBadge(row.status)}>{formatStatus(row.status)}</span>
                      </td>
                      <td>
                        <span className="sa-dispute-date">{formatDateTime(row.createdAt)}</span>
                      </td>
                      <td>
                        {isAssigned ? (
                          <span className="sa-dispute-badge sa-dispute-badge--manual">Assigned</span>
                        ) : null}
                      </td>
                      <td>
                        {isAssigned && row.assignedToName ? (
                          <div className="sa-dispute-support-user">
                            <span className="sa-dispute-avatar">
                              {nameInitials(row.assignedToName)}
                            </span>
                            <div className="sa-dispute-stack">
                              <span
                                className="sa-dispute-stack__title"
                                title={row.assignedToName}
                              >
                                {truncateText(row.assignedToName, 18)}
                              </span>
                              <span
                                className="sa-dispute-stack__meta"
                                title={row.assignedToEmail || undefined}
                              >
                                {truncateText(row.assignedToEmail || "Support", 22)}
                              </span>
                            </div>
                          </div>
                        ) : null}
                      </td>
                      <td>
                        <div className="sa-dispute-actions">
                          <button
                            type="button"
                            className="sa-dispute-icon-btn"
                            title="View details"
                            disabled={busyId === row.id}
                            onClick={() => handleView(row)}
                          >
                            <LuEye aria-hidden />
                          </button>
                          {canShowAssignAction ? (
                            <button
                              type="button"
                              className={`sa-dispute-icon-btn ${
                                isUnassigned
                                  ? "sa-dispute-icon-btn--assign"
                                  : "sa-dispute-icon-btn--reassign"
                              }`}
                              title={isUnassigned ? "Assign feedback" : "Reassign feedback"}
                              disabled={busyId === row.id}
                              onClick={() => openAssignModal(row)}
                            >
                              {isUnassigned ? (
                                <LuUserPlus aria-hidden />
                              ) : (
                                <LuUserCog aria-hidden />
                              )}
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <footer className="sa-access-footer">
          <span>
            Showing {items.length ? (currentPage - 1) * pageSize + 1 : 0} to{" "}
            {Math.min(currentPage * pageSize, total)} of {total} feedback
          </span>
          <div className="sa-access-pagination">
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => setPage((value) => Math.max(1, value - 1))}
            >
              <LuChevronLeft aria-hidden />
            </button>
            {pageNumbers.map((item) => (
              <button
                key={item}
                type="button"
                className={currentPage === item ? "is-active" : ""}
                onClick={() => setPage(item)}
              >
                {item}
              </button>
            ))}
            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
            >
              <LuChevronRight aria-hidden />
            </button>
            <select
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
            >
              {PAGE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size} per page
                </option>
              ))}
            </select>
          </div>
        </footer>
      </section>

      {viewFeedback?.feedback
        ? createPortal(
            <div
              className="sa-access-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="sa-feedback-modal-title"
              onClick={closeQuickView}
            >
              <div
                className="sa-access-modal__panel sa-dispute-modal__panel"
                onClick={(event) => event.stopPropagation()}
              >
                <header className="sa-dispute-modal__head">
                  <div className="sa-dispute-modal__head-main">
                    <div className="sa-dispute-modal__title-row">
                      <h2 id="sa-feedback-modal-title">{viewFeedback.feedback.feedbackNumber}</h2>
                      <span className="sa-dispute-id-chip">
                        #{String(viewFeedback.feedback.id).padStart(6, "0")}
                      </span>
                    </div>
                    <p className="sa-dispute-modal__subject">{viewFeedback.feedback.subject}</p>
                    <div className="sa-dispute-modal__badges">
                      <span className="sa-dispute-badge">
                        {viewFeedback.feedback.feedbackType || "—"}
                      </span>
                      <span className={statusBadge(viewFeedback.feedback.status)}>
                        {formatStatus(viewFeedback.feedback.status)}
                      </span>
                      {viewFeedback.feedback.assignmentStatus === "assigned" ? (
                        <span className="sa-dispute-badge sa-dispute-badge--manual">Assigned</span>
                      ) : null}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="sa-dispute-icon-btn"
                    aria-label="Close"
                    onClick={closeQuickView}
                  >
                    <LuX aria-hidden />
                  </button>
                </header>

                <div className="sa-dispute-modal__body">
                  {viewLoading ? (
                    <p className="sa-dispute-modal__description sa-dispute-modal__description--muted">
                      Loading feedback details...
                    </p>
                  ) : null}
                  {viewError ? (
                    <p className="sa-dispute-modal__description sa-dispute-modal__description--muted">
                      {viewError}
                    </p>
                  ) : null}

                  <section className="sa-dispute-modal__section">
                    <h3>Feedback Details</h3>
                    <div className="sa-dispute-modal__fields">
                      <div className="sa-dispute-modal__field">
                        <span>Type</span>
                        <strong>{viewFeedback.feedback.feedbackType || "—"}</strong>
                      </div>
                      <div className="sa-dispute-modal__field">
                        <span>Area</span>
                        <strong>{viewFeedback.feedback.areaLabel || "—"}</strong>
                      </div>
                      <div className="sa-dispute-modal__field">
                        <span>Rating</span>
                        <strong>{viewFeedback.feedback.rating || 0} / 5</strong>
                      </div>
                      <div className="sa-dispute-modal__field">
                        <span>Submitted</span>
                        <strong>{formatDateTime(viewFeedback.feedback.createdAt)}</strong>
                      </div>
                    </div>
                  </section>

                  <section className="sa-dispute-modal__section">
                    <h3>Description</h3>
                    <p className="sa-dispute-modal__description">
                      {viewFeedback.feedback.description || "No description provided."}
                    </p>
                  </section>

                  <section className="sa-dispute-modal__section">
                    <h3>Customer</h3>
                    <div className="sa-dispute-modal__parties">
                      <article className="sa-dispute-party-card">
                        <span className="sa-dispute-party-card__label">Submitter</span>
                        <div className="sa-dispute-support-user">
                          <span className="sa-dispute-avatar">
                            {nameInitials(viewFeedback.feedback.customerName)}
                          </span>
                          <div className="sa-dispute-stack">
                            <span className="sa-dispute-stack__title">
                              {viewFeedback.feedback.customerName || "—"}
                            </span>
                            <span className="sa-dispute-stack__meta">
                              {viewFeedback.feedback.customerEmail || "—"}
                            </span>
                          </div>
                        </div>
                      </article>
                    </div>
                  </section>

                  <section className="sa-dispute-modal__section">
                    <h3>Attachments</h3>
                    {Array.isArray(viewFeedback.attachments) &&
                    viewFeedback.attachments.length > 0 ? (
                      <div style={{ display: "grid", gap: 10 }}>
                        {viewFeedback.attachments.map((file) => (
                          <a
                            key={file.id}
                            className="sa-dispute-attachment"
                            href={resolvePublicFileUrl(file.filePath)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <span className="sa-dispute-attachment__icon" aria-hidden>
                              <LuPaperclip />
                            </span>
                            <span className="sa-dispute-attachment__meta">
                              <strong>{file.fileName || "View attachment"}</strong>
                              <small>Open / download file</small>
                            </span>
                          </a>
                        ))}
                      </div>
                    ) : (
                      <p className="sa-dispute-modal__description sa-dispute-modal__description--muted">
                        No attachments submitted with this feedback.
                      </p>
                    )}
                  </section>
                </div>

                <footer className="sa-access-modal__footer sa-dispute-modal__footer">
                  <button
                    type="button"
                    className="sa-access-btn sa-access-btn--ghost"
                    onClick={closeQuickView}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className="sa-access-btn sa-access-btn--ghost"
                    onClick={() => {
                      closeQuickView();
                      handleView(viewFeedback.feedback);
                    }}
                  >
                    Open full details
                  </button>
                  {canAssign &&
                  String(viewFeedback.feedback.status || "").toLowerCase() !== "closed" ? (
                    <button
                      type="button"
                      className="sa-access-btn sa-access-btn--primary"
                      onClick={() => {
                        openAssignModal(viewFeedback.feedback);
                        closeQuickView();
                      }}
                    >
                      {viewFeedback.feedback.assignmentStatus === "assigned"
                        ? "Reassign"
                        : "Assign"}
                    </button>
                  ) : null}
                </footer>
              </div>
            </div>,
            document.body,
          )
        : null}

      {assignTarget
        ? createPortal(
            <div
              className="sa-access-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="sa-feedback-assign-title"
              onClick={() => setAssignTarget(null)}
            >
              <div
                className="sa-access-modal__panel sa-dispute-assign-modal"
                onClick={(event) => event.stopPropagation()}
              >
                <header className="sa-dispute-assign-modal__head">
                  <div className="sa-dispute-assign-modal__title-wrap">
                    <span
                      className={`sa-dispute-assign-modal__icon ${
                        assignTarget.assignmentStatus === "assigned"
                          ? "sa-dispute-assign-modal__icon--reassign"
                          : "sa-dispute-assign-modal__icon--assign"
                      }`}
                      aria-hidden
                    >
                      {assignTarget.assignmentStatus === "assigned" ? (
                        <LuUserCog />
                      ) : (
                        <LuUserPlus />
                      )}
                    </span>
                    <div className="sa-dispute-modal__head-main">
                      <h2 id="sa-feedback-assign-title">
                        {assignTarget.assignmentStatus === "assigned"
                          ? "Reassign Feedback"
                          : "Assign Feedback"}
                      </h2>
                      <p className="sa-dispute-assign-modal__lead">
                        Choose an available Support User from your team.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="sa-dispute-icon-btn"
                    aria-label="Close"
                    onClick={() => setAssignTarget(null)}
                  >
                    <LuX aria-hidden />
                  </button>
                </header>

                <div className="sa-dispute-assign-modal__body">
                  <div className="sa-dispute-assign-modal__summary">
                    <div className="sa-dispute-assign-modal__summary-top">
                      <strong>{assignTarget.feedbackNumber}</strong>
                      <span className={assignmentBadge(assignTarget.assignmentStatus).className}>
                        {assignmentBadge(assignTarget.assignmentStatus).label}
                      </span>
                    </div>
                    <p title={assignTarget.subject || undefined}>
                      {assignTarget.subject || "No subject provided"}
                    </p>
                    {assignTarget.assignmentStatus === "assigned" && assignTarget.assignedToName ? (
                      <small>
                        Currently assigned to <em>{assignTarget.assignedToName}</em>
                      </small>
                    ) : null}
                  </div>

                  <label className="sa-access-field sa-dispute-assign-modal__field">
                    <span>
                      Assign To <em>*</em>
                    </span>
                    <select
                      value={assigneeId}
                      onChange={(event) => setAssigneeId(event.target.value)}
                    >
                      <option value="">Select an assignee</option>
                      <optgroup label="Support Users">
                        {assignees
                          .filter((user) => user.accountType !== "support_manager")
                          .map((user) => (
                            <option key={`support-${user.id}`} value={user.id}>
                              {user.name} ({user.email})
                            </option>
                          ))}
                      </optgroup>
                    </select>
                  </label>

                  <label className="sa-access-field sa-dispute-assign-modal__field">
                    <span>Reason (optional)</span>
                    <input
                      type="text"
                      value={assignReason}
                      onChange={(event) => setAssignReason(event.target.value)}
                      placeholder="Why assign this feedback?"
                    />
                  </label>

                  <label className="sa-access-field sa-dispute-assign-modal__field">
                    <span>Notes (optional)</span>
                    <textarea
                      value={assignNotes}
                      onChange={(event) => setAssignNotes(event.target.value)}
                      rows={3}
                      placeholder="Internal assignment notes"
                    />
                  </label>
                </div>

                <footer className="sa-dispute-assign-modal__footer">
                  <button
                    type="button"
                    className="sa-access-btn sa-access-btn--ghost"
                    onClick={() => setAssignTarget(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="sa-access-btn sa-access-btn--primary"
                    disabled={assigning || busyId === assignTarget.id || !assigneeId}
                    onClick={submitAssign}
                  >
                    {assigning ? (
                      "Assigning…"
                    ) : assignTarget.assignmentStatus === "assigned" ? (
                      <>
                        <LuUserCog aria-hidden /> Reassign
                      </>
                    ) : (
                      <>
                        <LuUserPlus aria-hidden /> Assign Feedback
                      </>
                    )}
                  </button>
                </footer>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
